<template>
  <div
    class="sm-table-row border-b border-gray-200 dark:border-gray-700"
    :class="[
      {
        '!border-b-0': props.isLastRow,
        'cursor-pointer': props.isClickable,
      },
    ]">
    <slot name="selection" :row="props.data">
      <div
        v-if="props.selectable"
        class="sm-table-row-select"
        @click="handleCellClick(-1, props.data, 'selection')">
        <input
          v-model="isChecked"
          type="checkbox"
          :disabled="props.selectDisabled"
          @click.stop />
      </div>
    </slot>

    <div v-if="props.expandable" class="self-center pl-2">
      <ChevronDown
        ref="expandIcon"
        :size="20"
        class="cursor-pointer transition-all duration-200 ease-in-out"
        :class="{ invisible: !hasExpandContent, '-rotate-90': !isExpanded }"
        @click="expandRow()" />
    </div>

    <div v-if="props.draggable" class="handle sm-table-drag-handler">
      <GripVertical
        :size="24"
        class="text-contrast-muted transition hover:text-contrast" />
    </div>

    <div
      v-for="(column, idx) in props.columns"
      :key="column.key"
      :class="[`cell-${column.key}`, 'sm-table-row-cell']"
      :style="{
        ...(idx === 0 && !selectable ? { paddingLeft: '10px' } : {}),
        ...cellStyle(column),
      }"
      @click="handleCellClick(idx, props.data, column.key)">
      <!-- Loader -->
      <SmSkeleton v-if="props.loading && column.loader" />
      <div v-else-if="props.loading">
        <!-- show nothing -->
      </div>

      <slot v-else :name="column.key" :row="props.data">
        <span
          v-if="
            props.data[column.key] && typeof props.data[column.key] === 'string'
          ">
          <TextMultiline
            :text="props.data[column.key] as string"
            tooltip-max-width="300px" />
        </span>
        <span v-else class="no-data">{{ i18n.t('noData') }}</span>
      </slot>
    </div>
  </div>
  <div
    ref="exapandWrapper"
    class="sm-tablerow-expand-wrapper max-h-0 overflow-hidden border-gray-200 transition-all duration-300 ease-in-out dark:border-gray-700"
    :class="{ 'border-b': isExpanded }">
    <slot name="expand" :row="props.data" :active="true"> </slot>
  </div>
</template>

<script setup generic="T extends TableData" lang="ts">
  import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    useSlots,
    watch,
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Column } from './SmTable.types'
  import TextMultiline from './TextMultiline.vue'
  import { TableData } from './SmTable.vue'
  import SmSkeleton from './SmSkeleton.vue'
  import { ChevronDown, GripVertical } from 'lucide-vue-next'

  const i18n = useI18n()
  const slots = useSlots()

  interface Props {
    keyField: keyof T
    data: T
    columns: Column<T>[]
    selectable?: boolean
    selectDisabled?: boolean
    draggable?: boolean
    isLastRow?: boolean
    isClickable?: boolean
    expandable?: boolean
    loading?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    selectable: false,
    draggable: false,
    isChecked: false,
    isLastRow: false,
    isClickable: false,
    expandable: false,
    loading: false,
  })

  const isChecked = defineModel<boolean>('isChecked')

  const emit = defineEmits<{
    select: [item: T]
    deselect: [item: T]
    'handle-cell-click': [{ iRow: number; columnKey: string; row: T }]
    expand: [row: T, expanded: boolean]
  }>()

  function handleCellClick(iRow: number, row: T, columnKey: string) {
    if (columnKey === 'selection' && !props.selectDisabled) {
      isChecked.value = !isChecked.value
    }
    emit('handle-cell-click', { iRow, columnKey, row })
  }

  watch(
    () => isChecked.value,
    () => {
      if (isChecked.value) {
        emit('select', props.data)
      } else {
        emit('deselect', props.data)
      }
    }
  )

  const columnStyleCache: Record<string, Record<string, number | string>> = {}
  function cellStyle(column: Column<T>) {
    // Check cache
    if (columnStyleCache[column.key]) {
      return columnStyleCache[column.key]
    }

    const style: Record<string, number | string> = {}

    // Get width
    if (typeof column.width === 'number') {
      style['flex'] = `${column.width}`
    } else {
      style['width'] = column.width
    }

    // set alignment
    if (column.align) {
      style['justify-content'] = column.align
    }

    // Cache style
    if (!columnStyleCache[column.key]) {
      columnStyleCache[column.key] = {}
    }
    columnStyleCache[column.key] = style

    // Return style
    return style
  }

  // Expander Slot

  const exapandWrapper = ref<HTMLElement | null>(null)
  const isExpanded = ref<boolean>(false)
  const resizeObserver = ref<ResizeObserver | null>(null)
  const rowKey = computed(() => props.data?.[props.keyField])

  function expandRow() {
    if (exapandWrapper.value) {
      if (
        exapandWrapper.value.style.maxHeight &&
        exapandWrapper.value.style.maxHeight !== '0px'
      ) {
        isExpanded.value = false
        exapandWrapper.value.style.maxHeight = '0'
      } else {
        isExpanded.value = true
        exapandWrapper.value.style.maxHeight =
          exapandWrapper.value.scrollHeight + 'px'
      }
      emit('expand', props.data, isExpanded.value)
    }
  }

  onMounted(() => {
    if (exapandWrapper.value) {
      resizeObserver.value = new ResizeObserver(() => {
        if (isExpanded.value && exapandWrapper.value) {
          exapandWrapper.value.style.maxHeight =
            exapandWrapper.value.scrollHeight + 'px'
        }
      })
      resizeObserver.value.observe(exapandWrapper.value)
    }
  })

  onUnmounted(() => {
    resizeObserver.value?.disconnect()
  })

  watch(rowKey, () => {
    isExpanded.value = false
    if (exapandWrapper.value) {
      exapandWrapper.value.style.maxHeight = '0px'
    }
  })

  const hasExpandContent = computed(() => {
    if (slots.expand) {
      const expandSlot = slots.expand()
      return !!expandSlot[0].children?.length
    }
  })
</script>

<style lang="scss">
  $gap: 1.5rem;
  $select-width: 40px;

  .sm-table-row {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    border-bottom: 1px solid var(--el-border-color-lighter);
    min-height: 52px;

    // Remove link underline
    text-decoration: none;
    color: inherit;

    &:hover {
      background-color: var(--el-fill-color-light);
    }
  }

  .sm-table-row-select {
    width: $select-width;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sm-table-row-cell {
    display: flex;
    align-items: center;
    justify-content: left;
    color: var(--el-table-text-color);
    padding: 0 calc($gap / 2);
  }

  .sm-table-drag-handler {
    cursor: grab !important;
    display: flex;
    align-items: center;
  }

  .sm-table-row.sortable-chosen {
    cursor: grabbing !important;
  }
</style>
